<script lang="ts" setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { faBath, faBed, faRulerTriangle } from '@fortawesome/pro-solid-svg-icons';
import SectionHeader from '@/components/SectionHeader.vue';
import { Constants } from '@/utilities/constants.ts';
import type { GetProjectDetailsProjectResponse } from '@/api';
import { getBedroomsLabel } from '@/views/projects/bedrooms.ts';

const props = defineProps<{
  project: GetProjectDetailsProjectResponse;
}>();

const emit = defineEmits<{
  (e: 'click', id: number): void;
}>();

const { t, locale } = useI18n();
const router = useRouter();

type SortKey = 'default' | 'price-ascending' | 'price-descending' | 'bedrooms' | 'floor-area';

const sortKey = ref<SortKey>('default');

// Trier trois ou quatre modèles n'apporte rien : la commande n'apparaît qu'à partir du seuil.
const SORT_THRESHOLD = 5;
const showSort = computed(() => props.project.models.length >= SORT_THRESHOLD);

const sortedModels = computed(() => {
  const models = [...props.project.models];

  switch (sortKey.value) {
    case 'price-ascending':
      return models.sort((a, b) => (a.price.amount || Number.MAX_SAFE_INTEGER) - (b.price.amount || Number.MAX_SAFE_INTEGER));
    case 'price-descending':
      return models.sort((a, b) => b.price.amount - a.price.amount);
    case 'bedrooms':
      return models.sort((a, b) => (b.bedroomsMax ?? b.bedrooms ?? -1) - (a.bedroomsMax ?? a.bedrooms ?? -1));
    case 'floor-area':
      return models.sort((a, b) => b.floorArea - a.floorArea);
    default:
      return models;
  }
});

const baseUrl = computed(() => router.resolve({ name: 'project', params: { slug: props.project.name.toSlug(), projectId: props.project.id } }).fullPath);
const cardSizes = `(max-width: ${Constants.Breakpoints.md}) 40vw, ${Constants.ImageSizes.Small.Width}px`;

function modelUrl(model: GetProjectDetailsProjectResponse['models'][number]) {
  return `${baseUrl.value}#${model.name.toSlug()}-${model.id}`;
}
</script>

<template>
  <section v-if="project.models.length > 0">
    <SectionHeader>
      {{ t('views.project.models.header') }}
      <template v-if="showSort" #actions>
        <label class="no-print flex items-center gap-2 text-sm font-normal text-gray-700">
          <span>{{ t('views.project.models.sort') }}</span>
          <select v-model="sortKey" class="focus:outline-ph-green-600 cursor-pointer rounded-md border-none bg-white py-1 pr-8 pl-2 text-sm text-gray-900 outline-1 -outline-offset-2 outline-gray-300 focus:outline-2 focus:-outline-offset-2">
            <option value="default">{{ t('views.project.models.sort-default') }}</option>
            <option value="price-ascending">{{ t('views.project.models.sort-price-ascending') }}</option>
            <option value="price-descending">{{ t('views.project.models.sort-price-descending') }}</option>
            <option value="bedrooms">{{ t('views.project.models.sort-bedrooms') }}</option>
            <option value="floor-area">{{ t('views.project.models.sort-floor-area') }}</option>
          </select>
        </label>
      </template>
    </SectionHeader>

    <!-- Carte couchée : la photo tient le tiers gauche et les données occupent le reste. Le nombre
         de colonnes n'augmente pas de façon continue : la colonne de contact s'installe à partir de
         lg et rétrécit la section, qui ne retrouve la largeur de deux cartes qu'à partir de xl. -->
    <ul class="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-1 xl:grid-cols-2">
      <li v-for="model in sortedModels" :key="model.id" class="hover:outline-ph-green-500 group relative flex min-h-38 overflow-hidden rounded-xl outline outline-gray-200 transition-[outline-color,box-shadow] hover:shadow-lg">
        <div class="relative w-[36%] max-w-65 shrink-0 self-stretch overflow-hidden bg-gray-100" :class="{ grayscale: model.isSold }">
          <img
            v-if="model.images.length > 0 && model.images[0]"
            :alt="model.images[0].title || model.name"
            :height="Constants.ImageSizes.Medium.Height"
            :sizes="cardSizes"
            :src="model.images[0].src.resizedToSmall()"
            :srcset="model.images[0].src.toSrcset()"
            :width="Constants.ImageSizes.Medium.Width"
            class="absolute inset-0 h-full w-full object-cover"
            decoding="async"
            loading="lazy"
          />
          <span v-if="model.isSold" class="absolute top-2 left-2 rounded-md bg-red-800/90 px-2 py-0.5 text-xs font-semibold text-white">
            {{ t('views.project.models.sold') }}
          </span>
        </div>

        <div class="flex min-w-0 flex-1 flex-col p-3 sm:p-4">
          <h3 class="group-hover:text-ph-green-600 line-clamp-2 text-base/5 font-bold tracking-tight text-gray-900 sm:text-lg/6">{{ model.name }}</h3>
          <p class="mt-0.5 line-clamp-1 text-xs text-gray-500 sm:text-sm">{{ model.category.name }}</p>

          <!-- Les deux premières colonnes ont une largeur fixe : les icônes tombent au même endroit
               d'une carte à l'autre et la superficie prend toute la place qui reste. -->
          <ul class="mt-2.5 grid grid-cols-[minmax(2.6rem,auto)_2.6rem_1fr] items-center gap-x-2.5 text-sm font-semibold text-gray-800 sm:mt-3 sm:grid-cols-[minmax(3.25rem,auto)_3.25rem_1fr] sm:gap-x-3 sm:text-base">
            <li class="flex items-center gap-1.5 whitespace-nowrap" :class="{ 'text-gray-300': model.bedrooms === null }" :title="t('views.project.stats.bedrooms')">
              <FontAwesomeIcon :icon="faBed" class="h-4 shrink-0 text-gray-400 sm:h-4.5" />
              <span aria-hidden="true"
                >{{ model.bedrooms === null ? '–' : model.bedrooms === 0 ? t('views.project.models.studio') : model.bedrooms
                }}<template v-if="model.bedrooms !== null && (model.bedroomsMax ?? null) !== null">&ndash;{{ model.bedroomsMax }}</template></span
              >
              <span class="sr-only">{{ getBedroomsLabel(t, model) }}</span>
            </li>
            <li class="flex items-center gap-1.5 whitespace-nowrap" :class="{ 'text-gray-300': model.bathrooms <= 0 }" :title="t('views.project.stats.bathrooms')">
              <FontAwesomeIcon :icon="faBath" class="h-4 shrink-0 text-gray-400 sm:h-4.5" />
              <span aria-hidden="true">{{ model.bathrooms > 0 ? model.bathrooms : '–' }}</span>
              <span class="sr-only">{{ t('views.project.characteristics.bathrooms', model.bathrooms) }}</span>
            </li>
            <li class="flex items-center gap-1.5 whitespace-nowrap" :class="{ 'text-gray-300': model.floorArea <= 0 }" :title="t('views.project.stats.floor-area')">
              <FontAwesomeIcon :icon="faRulerTriangle" class="h-4 shrink-0 text-gray-400 sm:h-4.5" />
              <span>{{ model.floorArea > 0 ? `${model.floorArea.formatNumber(locale)} ${t('views.project.characteristics.floor-area')}` : '–' }}</span>
            </li>
          </ul>

          <p v-if="project.isPaid" class="mt-auto pt-3 text-gray-800">
            <template v-if="model.price.amount > 0">
              <span class="flex items-baseline gap-1.5 whitespace-nowrap">
                <span v-if="model.price.isStarting" class="text-xs text-gray-500 sm:text-sm">{{ t('views.project.models.starting-from') }}</span>
                <span class="text-lg font-bold tracking-tight text-gray-900 sm:text-xl">{{ model.price.amount.formatCurrency(locale) }}</span>
                <span v-if="model.price.type.id == Constants.PriceTypeId.ForRent" class="text-xs font-medium text-gray-500">{{ t('views.project.models.per-month-short') }}</span>
              </span>
              <span v-if="model.price.priceTaxes === 'included'" class="block text-xs text-gray-500">{{ t('views.project.models.taxes-included') }}</span>
              <span v-if="model.price.priceTaxes === 'excluded'" class="block text-xs text-gray-500">{{ t('views.project.models.taxes-excluded') }}</span>
            </template>
            <span v-else class="text-sm font-semibold text-gray-600">{{ t('views.project.models.upon-request') }}</span>
          </p>
        </div>

        <a :href="modelUrl(model)" class="focus-within:ring-ph-green-500 absolute inset-0 cursor-pointer rounded-xl focus-within:ring-2 focus-within:outline-hidden" @click.prevent="emit('click', model.id)">
          <span class="sr-only">{{ model.name }}</span>
        </a>
      </li>
    </ul>

    <p v-if="project.isPaid" class="mt-2 text-gray-600 italic">
      <small>{{ t('views.project.models.price-disclaimer') }}</small>
    </p>
  </section>
</template>

<style lang="css" scoped></style>
